import React, { useState } from "react";

import { Grid, IconButton, MenuItem, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IoCheckmarkOutline, IoClose } from "react-icons/io5";
import * as yup from "yup";

import { useMovingResources } from "../../../../../context/MovingResources";
import useYupValidation from "../../../../../hooks/useYupValidation";
import { ServiceBooking } from "../../../../../models/service.booking";
import { Select } from "../../../../Form";
import InputRange from "../../../../Form/InputRange";
import useStyles from "./styles";

type Props = {
  onCancel: () => void;
  onSave: (material: ServiceBooking, quantity: number) => void;
  packingMaterials?: ServiceBooking[];
  roomId?: number;
};

const FormMaterial = (props: Props) => {
  const { t: translate } = useTranslation();

  const { materialList } = useMovingResources();

  const { onSave, onCancel, packingMaterials, roomId } = props;

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const [quantity, setQuantity] = useState<number>(1);
  const [serviceBooking, setServiceBooking] = useState<ServiceBooking>();

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const materials = packingMaterials || materialList || [];

  const getFilteredMaterials = () => {
    if (!roomId || !materials || materials.length === 0) return materials;

    const filtered = materials.filter((material) =>
      material?.rooms?.some((room) => room?.id === roomId)
    );

    return filtered.length > 0 ? filtered : materials;
  };

  const materialsToRender = getFilteredMaterials();

  const classes = useStyles();

  const schema = yup.object().shape({
    serviceBookingId: yup
      .number()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    quantity: yup
      .number()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
  });

  const handleValidate = (): boolean => {
    try {
      schema.validateSync(
        {
          serviceBookingId: serviceBooking?.id,
          quantity,
        },
        { abortEarly: false }
      );
      setErrors([]);
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }

    return true;
  };

  const renderOptionsMaterial = () => {
    if (materialsToRender && materialsToRender?.length > 0) {
      return materialsToRender.map((material) => (
        <MenuItem key={material.id} value={material.id}>
          {material.name}
        </MenuItem>
      ));
    }
  };

  return (
    <Grid container item alignItems="flex-start" justifyContent="space-between" spacing={2}>
      <Grid item xs={8}>
        <Select
          name="material"
          label={translate("material")}
          onChange={(event) => {
            if (materials && materials?.length > 0) {
              const materialFind = materials.find(
                (material) => material?.id === event.target.value
              );

              setServiceBooking(materialFind);
            }
          }}
          displayEmpty
          placeholder={`${translate("selectAMaterial")}...`}
          value={serviceBooking?.id}
          error={fieldHasError("serviceBookingId")}
          errorMessage={getErrorByField("serviceBookingId")}
        >
          {renderOptionsMaterial()}
        </Select>
      </Grid>
      <Grid item xs={3}>
        <InputRange
          label={translate("quantity")}
          value={quantity}
          change={(value: number) => setQuantity(value)}
          error={fieldHasError("quantity")}
          errorMessage={getErrorByField("quantity")}
          range={1}
          onlyIntValues
        />
      </Grid>
      <Grid
        container
        justifyContent="flex-end"
        alignItems="center"
        item
        xs={1}
        className={classes.actionsAddingMaterial}
      >
        <Tooltip placement="top" title={translate("addMaterial")}>
          <IconButton
            onClick={() => {
              if (handleValidate()) onSave(serviceBooking!, quantity);
            }}
            className={classes.iconButton}
          >
            <IoCheckmarkOutline color="#12B76A" size={24} />
          </IconButton>
        </Tooltip>
        <Tooltip placement="top" title={translate("discard")}>
          <IconButton onClick={onCancel} className={classes.iconButton}>
            <IoClose color="#475467" size={24} />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

export default FormMaterial;
